import { ReactElement, useState } from 'react';
import { Device } from '@/api';
import {
    UIButton,
    UISection,
    UISectionContent,
    UISectionHeader,
    useUIConfirmationModal
} from '@/ui';
import {
    useCancelDevicePowerOffAction,
    useCancelDeviceRebootAction,
    usePowerOffDeviceAction,
    useRebootDeviceAction
} from '../actions';
import { useDevicesTranslation } from '../hooks';
import { PendingBadge } from './pending-badge';
import { SectionRow } from './section-row';
import { TestCallDeviceModal } from './test-call-device-modal';

export type DeviceActionsSectionProps = {
    device: Device;
    /** Whether the current user may edit this device. */
    canEdit: boolean;
};

/**
 * "Actions" section of the device page: reboot, turn off and test the device.
 * Reboot/power-off are async device commands — when one is pending we surface
 * it and offer a cancel. Hidden for users who can't edit the device.
 */
export function DeviceActionsSection(
    props: DeviceActionsSectionProps
): ReactElement | null {
    const { device, canEdit } = props;
    const { t } = useDevicesTranslation();

    const confirmationModal = useUIConfirmationModal();
    const { rebootDevice } = useRebootDeviceAction({ deviceId: device.id });
    const { powerOffDevice } = usePowerOffDeviceAction({
        deviceId: device.id
    });
    const { cancelDeviceReboot } = useCancelDeviceRebootAction({
        deviceId: device.id
    });
    const { cancelDevicePowerOff } = useCancelDevicePowerOffAction({
        deviceId: device.id
    });

    const [isTestOpen, setIsTestOpen] = useState(false);

    const pendingReboot = device.commands?.find(
        (c) => c.type === 'reboot' && c.status === 'pending'
    );
    const pendingPowerOff = device.commands?.find(
        (c) => c.type === 'power_off' && c.status === 'pending'
    );

    if (!canEdit) {
        return null;
    }

    function confirm(opts: {
        title: string;
        confirmText: string;
        confirmVariant: 'primary' | 'danger';
        run: () => Promise<unknown>;
    }) {
        confirmationModal.open({
            title: opts.title,
            confirmText: opts.confirmText,
            confirmVariant: opts.confirmVariant,
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                opts.run()
                    .catch(() => {}) // Error handled in the action
                    .finally(() => confirmationModal.close());
            },
            onCancel: () => confirmationModal.close()
        });
    }

    return (
        <>
            <UISection>
                <UISectionHeader
                    title={t('components.device_actions_section.title')}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <SectionRow
                            label={t('shared.devices.test_connectivity_label')}
                            description={t(
                                'components.device_actions_section.test_description'
                            )}
                        >
                            <UIButton
                                text={t('shared.labels.test')}
                                leftIcon="activity"
                                onClick={() => setIsTestOpen(true)}
                            />
                        </SectionRow>

                        <SectionRow
                            label={t('shared.devices.reboot_label')}
                            description={t(
                                'components.device_actions_section.reboot_description'
                            )}
                        >
                            {pendingReboot ? (
                                <PendingBadge
                                    label={t(
                                        'components.device_actions_section.reboot_pending'
                                    )}
                                    tooltip={t(
                                        'shared.devices.cancel_pending_action_confirm'
                                    )}
                                    onCancel={() =>
                                        confirm({
                                            title: t(
                                                'shared.devices.cancel_pending_action_confirm_title'
                                            ),
                                            confirmText: t(
                                                'shared.devices.cancel_pending_action_confirm'
                                            ),
                                            confirmVariant: 'danger',
                                            run: cancelDeviceReboot
                                        })
                                    }
                                />
                            ) : (
                                <UIButton
                                    text={t('shared.devices.reboot_label')}
                                    onClick={() =>
                                        confirm({
                                            title: t(
                                                'shared.devices.reboot_confirm_title'
                                            ),
                                            confirmText: t(
                                                'shared.devices.reboot_label'
                                            ),
                                            confirmVariant: 'primary',
                                            run: rebootDevice
                                        })
                                    }
                                />
                            )}
                        </SectionRow>

                        <SectionRow
                            label={t('shared.devices.power_off_label')}
                            description={t(
                                'components.device_actions_section.power_off_description'
                            )}
                        >
                            {pendingPowerOff ? (
                                <PendingBadge
                                    label={t(
                                        'components.device_actions_section.power_off_pending'
                                    )}
                                    tooltip={t(
                                        'shared.devices.cancel_pending_action_confirm'
                                    )}
                                    onCancel={() =>
                                        confirm({
                                            title: t(
                                                'shared.devices.cancel_pending_action_confirm_title'
                                            ),
                                            confirmText: t(
                                                'shared.devices.cancel_pending_action_confirm'
                                            ),
                                            confirmVariant: 'danger',
                                            run: cancelDevicePowerOff
                                        })
                                    }
                                />
                            ) : (
                                <UIButton
                                    text={t('shared.devices.power_off_label')}
                                    variant="danger"
                                    onClick={() =>
                                        confirm({
                                            title: t(
                                                'shared.devices.power_off_confirm_title'
                                            ),
                                            confirmText: t(
                                                'shared.devices.power_off_label'
                                            ),
                                            confirmVariant: 'danger',
                                            run: powerOffDevice
                                        })
                                    }
                                />
                            )}
                        </SectionRow>
                    </div>
                </UISectionContent>
            </UISection>
            <TestCallDeviceModal
                deviceId={device.id}
                isOpen={isTestOpen}
                onClose={() => setIsTestOpen(false)}
            />
        </>
    );
}
